import React, { useState, useEffect, useCallback } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    Table,
    TextFilter,
    Pagination,
    Badge,
    StatusIndicator,
    Link,
    Select,
    Icon,
    Alert
} from '@amzn/awsui-components-console';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { useProfile } from '../../hooks/useProfiles';
import NProgress from 'nprogress';
import './JobsPage.scss';

interface Job {
    jobId: string;
    jobName: string;
    description?: string;
    profileId: string;
    status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
    templateId?: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

const JobsPage: React.FC = () => {
    const { id: profileId } = useParams();
    const navigate = useNavigate();
    const [authenticatedUser] = useAtom(getUserAtom);
    
    // Fetch profile data from API
    const { profile: profileData, loading: profileLoading, error: profileError } = useProfile(profileId || null);
    
    // State management
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filteringText, setFilteringText] = useState('');
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [sortingColumn, setSortingColumn] = useState<any>({
        sortingField: 'createdAt',
        sortingDescending: true
    });
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [runningJobId, setRunningJobId] = useState<string | null>(null);
    
    // Fetch jobs from API
    const fetchJobs = useCallback(async (loadMore = false) => {
        if (!profileId || !authenticatedUser) return;
        
        setLoading(true);
        setError(null);
        NProgress.start();
        
        try {
            
            const response = await makeApiCall(
                API_PATHS.JOBS.LIST,
                'POST',
                {
                    profileId: profileId,
                    limit: 50,
                    ...(loadMore && nextToken ? { nextToken } : {})
                }
            );
            
            if (response.jobs) {
                const transformedJobs = response.jobs.map((job: any) => ({
                    ...job,
                    // Ensure consistent field names
                    id: job.jobId,
                    name: job.jobName
                }));
                
                if (loadMore) {
                    setJobs(prev => [...prev, ...transformedJobs]);
                } else {
                    setJobs(transformedJobs);
                }
                
                setNextToken(response.nextToken || null);
                setHasMore(!!response.nextToken);
            }
        } catch (err: any) {
            console.error('Failed to fetch jobs:', err);
            setError(err.message || 'Failed to load jobs');
        } finally {
            setLoading(false);
            NProgress.done();
        }
    }, [profileId, authenticatedUser, nextToken]);
    
    // Initial load
    useEffect(() => {
        fetchJobs();
    }, [profileId, authenticatedUser?.username]);
    
    // Filter jobs based on search text and status
    const filteredJobs = jobs.filter(job => {
        // Text filter - handle null/undefined values safely
        const searchText = filteringText.toLowerCase();
        const matchesText = !filteringText || 
                           (job.jobName?.toLowerCase().includes(searchText) ?? false) ||
                           (job.createdBy?.toLowerCase().includes(searchText) ?? false) ||
                           (job.description?.toLowerCase().includes(searchText) ?? false);
        
        // Status filter
        const matchesStatus = statusFilter === 'all' || 
                            (statusFilter === 'active' && job.status === 'ACTIVE') ||
                            (statusFilter === 'inactive' && job.status === 'INACTIVE');
        
        return matchesText && matchesStatus;
    });
    
    // Sort filtered jobs
    const sortedJobs = [...filteredJobs].sort((a, b) => {
        if (!sortingColumn?.sortingField) return 0;
        
        let aValue: any;
        let bValue: any;
        
        switch (sortingColumn.sortingField) {
            case 'jobName':
                aValue = a.jobName || '';
                bValue = b.jobName || '';
                break;
            case 'description':
                aValue = a.description || '';
                bValue = b.description || '';
                break;
            case 'status':
                aValue = a.status;
                bValue = b.status;
                break;
            case 'createdBy':
                aValue = a.createdBy || '';
                bValue = b.createdBy || '';
                break;
            case 'createdAt':
                aValue = new Date(a.createdAt).getTime();
                bValue = new Date(b.createdAt).getTime();
                break;
            default:
                return 0;
        }
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // Compare values
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;
        
        // Apply sort direction
        return sortingColumn.sortingDescending ? -comparison : comparison;
    });
    
    // Pagination
    const pageSize = 10;
    const pagesCount = Math.ceil(sortedJobs.length / pageSize);
    const currentPageItems = sortedJobs.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );
    
    // Handle sorting
    const handleSortingChange = (event: any) => {
        setSortingColumn(event.detail.sortingColumn);
    };
    
    // Refresh jobs
    const handleRefresh = () => {
        fetchJobs();
    };
    
    // Load more jobs
    const handleLoadMore = () => {
        if (hasMore && !loading) {
            fetchJobs(true);
        }
    };
    
    // Handle run job
    const handleRunJob = async (jobId: string) => {
        if (!jobId) {
            console.error('No job ID available');
            return;
        }
        
        setRunningJobId(jobId);
        setError(null);
        NProgress.start();
        
        try {
            
            const runJobPayload = {
                jobId,
                runType: 'NORMAL'
            };
            
            const response = await makeApiCall(API_PATHS.JOBS.RUN, 'POST', runJobPayload);
            
            // Show success message
            alert(`Job run started successfully! Run ID: ${response.runId || 'N/A'}`);
            
            // Navigate to job history to see the new run
            if (response.runId) {
                navigate(`/profile/${profileId}/job/${jobId}/history`);
            }
        } catch (error: any) {
            console.error('Error running job:', error);
            setError(error.message || 'Failed to run job. Please try again.');
        } finally {
            setRunningJobId(null);
            NProgress.done();
        }
    };
    
    // Status indicator component
    const StatusIcon = ({ status }: { status: string }) => {
        switch (status) {
            case 'ACTIVE':
                return <StatusIndicator type="success">Active</StatusIndicator>;
            case 'INACTIVE':
                return <StatusIndicator type="stopped">Inactive</StatusIndicator>;
            case 'DRAFT':
                return <StatusIndicator type="pending">Draft</StatusIndicator>;
            default:
                return <StatusIndicator>Unknown</StatusIndicator>;
        }
    };
    
    // Format date
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };
    
    // Table column definitions
    const columnDefinitions = [
        {
            id: 'name',
            header: 'Job Name',
            cell: (item: Job) => (
                <Link 
                    href="#" 
                    external={false}
                    onFollow={(e) => {
                        e.preventDefault();
                        navigate(`/profile/${profileId}/job/${item.jobId}`);
                    }}
                >
                    {item.jobName || 'Unnamed Job'}
                </Link>
            ),
            sortingField: 'jobName',
            isRowHeader: true,
            width: 250
        },
        {
            id: 'description',
            header: 'Description',
            cell: (item: Job) => item.description || '-',
            sortingField: 'description',
            width: 200
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item: Job) => <StatusIcon status={'ACTIVE'} />,
            sortingField: 'status',
            width: 120
        },
        {
            id: 'createdBy',
            header: 'Created By',
            cell: (item: Job) => item.createdBy ? (
                <Link href={`https://phonetool.amazon.com/users/${item.createdBy}`} external={true}>
                    @{item.createdBy}
                </Link>
            ) : '-',
            sortingField: 'createdBy',
            width: 150
        },
        {
            id: 'createdAt',
            header: 'Created',
            cell: (item: Job) => formatDate(item.createdAt),
            sortingField: 'createdAt',
            width: 180
        },
        {
            id: 'runHistory',
            header: 'Actions',
            cell: (item: Job) => (
                <SpaceBetween direction="horizontal" size="xs">
                    <Button iconName='history'
                        variant="link"
                        onClick={() => navigate(`/profile/${profileId}/job/${item.jobId}/history`)}
                    >
                        View Runs
                    </Button>
                    <Button
                        variant="normal"
                        onClick={() => handleRunJob(item.jobId)}
                        iconName="play"
                        loading={runningJobId === item.jobId}
                        disabled={runningJobId !== null}
                    >
                        Run Job
                    </Button>
                </SpaceBetween>
            ),
            width: 200
        }
    ];
    
    return (
        <div className="jobs-page">
            <ContentLayout
                defaultPadding={false}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
                        <SpaceBetween size="m" direction="vertical">
                            <BreadcrumbGroup
                                items={[
                                    { text: "Home", href: "/" },
                                    { text: profileData?.name || 'Loading...', href: `/profile/${profileId}` },
                                    { text: "Jobs", href: "#" }
                                ]}
                                ariaLabel="Breadcrumbs"
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                <div style={{ flex: 1 }}>
                                    <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                        {profileData?.name || 'Loading...'}
                                    </Box>
                                    <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                        {profileData?.description || `Jobs under: ${profileData?.name || 'this profile'}`}
                                    </Box>
                                    
                                    <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                            <Badge color="green">{profileData?.ldapGroup || 'No LDAP Group'}</Badge>
                                            <Box color="text-status-inactive">|</Box>
                                            <Link 
                                                href={`https://phonetool.amazon.com/users/${profileData?.createdBy}`}
                                                external={true}
                                                fontSize="body-m"
                                            >
                                                @{profileData?.createdBy || 'Unknown'}
                                            </Link>
                                            <Box color="text-status-inactive">|</Box>
                                            <Box color="text-body-secondary">
                                                Created: {profileData?.lastUpdated ? new Date(profileData.lastUpdated).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : 'N/A'}
                                            </Box>
                                        </SpaceBetween>
                                    </Box>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                    <Button 
                                        iconName="refresh"
                                        onClick={handleRefresh}
                                        variant="normal"
                                        loading={loading}
                                        ariaLabel="Refresh jobs"
                                    >
                                        
                                    </Button>
                                </div>
                            </div>
                        </SpaceBetween>
                    </Box>
                }
            >
                <Box padding={{ horizontal: "xl", vertical: "l" }}>
                    {/* Profile Error Alert */}
                    {profileError && (
                        <Alert
                            type="error"
                            dismissible
                            onDismiss={() => {}}
                            header="Error loading profile"
                        >
                            {profileError}
                        </Alert>
                    )}
                    
                    <Container
                        header={
                            <Header
                                variant="h2"
                                counter={`(${sortedJobs.length})`}
                                actions={
                                    <Pagination
                                        currentPageIndex={currentPageIndex}
                                        pagesCount={pagesCount}
                                        onChange={({ detail }) =>
                                            setCurrentPageIndex(detail.currentPageIndex)
                                        }
                                        ariaLabels={{
                                            nextPageLabel: 'Next page',
                                            previousPageLabel: 'Previous page',
                                            pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                                        }}
                                    />
                                }
                            >
                                Jobs
                            </Header>
                        }
                    >
                        <SpaceBetween size="l" direction="vertical">
                            {/* Error Alert */}
                            {error && (
                                <Alert
                                    type="error"
                                    dismissible
                                    onDismiss={() => setError(null)}
                                    header="Error loading jobs"
                                >
                                    {error}
                                </Alert>
                            )}
                            
                            {/* Search and Filter */}
                            <SpaceBetween size="s" direction="horizontal">
                                <TextFilter
                                    filteringText={filteringText}
                                    filteringPlaceholder="Find jobs by name, description, or creator"
                                    filteringAriaLabel="Filter jobs"
                                    onChange={({ detail }) => {
                                        setFilteringText(detail.filteringText);
                                        setCurrentPageIndex(1);
                                    }}
                                    countText={`${sortedJobs.length} matches`}
                                />
                                <Select
                                    selectedOption={{ label: statusFilter === 'all' ? 'All statuses' : statusFilter === 'active' ? 'Active' : 'Inactive', value: statusFilter }}
                                    onChange={({ detail }) => {
                                        setStatusFilter(detail.selectedOption.value || 'all');
                                        setCurrentPageIndex(1);
                                    }}
                                    options={[
                                        { label: 'All statuses', value: 'all' },
                                        { label: 'Active', value: 'active' },
                                        { label: 'Inactive', value: 'inactive' }
                                    ]}
                                    placeholder="Filter by status"
                                />
                            </SpaceBetween>
                            
                            {/* Jobs Table */}
                            <Table
                                columnDefinitions={columnDefinitions}
                                items={currentPageItems}
                                loading={loading}
                                loadingText="Loading jobs..."
                                onSortingChange={handleSortingChange}
                                sortingColumn={sortingColumn}
                                trackBy="jobId"
                                variant="container"
                                stickyHeader={true}
                                empty={
                                    <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xxl' }}>
                                        <SpaceBetween size="m">
                                            <Icon name="search" size="large" />
                                            <b>No jobs found</b>
                                            <Box>
                                                {filteringText || statusFilter !== 'all' 
                                                    ? 'Try adjusting your filters'
                                                    : 'Create your first job to get started'}
                                            </Box>
                                        </SpaceBetween>
                                    </Box>
                                }
                            />
                            
                            {/* Load More Button */}
                            {hasMore && !loading && (
                                <Box textAlign="center">
                                    <Button
                                        onClick={handleLoadMore}
                                        loading={loading}
                                        loadingText="Loading more..."
                                    >
                                        Load more jobs
                                    </Button>
                                </Box>
                            )}
                        </SpaceBetween>
                    </Container>
                    {/* Add spacing at bottom to prevent footer overlap */}
                    <Box padding={{ bottom: "xxxl" }} />
                </Box>
            </ContentLayout>
        </div>
    );
};

export default JobsPage;
